import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import MarkdownEditor from '~/components/Common/MarkdownEditor';

import { TourOfferItineraryDayInput } from '~/types/tours/tourOffer';

import NativeTourImageField from './NativeTourImageField';
import NativeTourLocationSearch, { locationLabel } from './NativeTourLocationSearch';
import NativeTourRemoveButton from './NativeTourRemoveButton';
import { ITINERARY_MEALS } from './nativeTourForm';

interface Props {
  day: TourOfferItineraryDayInput;
  /** The tour days it covers, from 1. */
  startDay: number;
  problem: string | null;
  hosts: Array<{ svcImageId: string; name: string }>;
  isFirst: boolean;
  isLast: boolean;
  expanded: boolean;
  onExpand: (expanded: boolean) => void;
  onChange: (day: TourOfferItineraryDayInput) => void;
  onMove: (by: -1 | 1) => void;
  onCopy: () => void;
  onRemove: () => void;
}

// As on the tour form: a call-out shows a host's image, the default host image, or none.
const NO_IMAGE = 'none';
const DEFAULT_HOST_IMAGE = 'default';

/** One entry of a tour option's itinerary, which can span several days. */
export default function NativeTourItineraryDay({
  day,
  startDay,
  problem,
  hosts,
  isFirst,
  isLast,
  expanded,
  onExpand,
  onChange,
  onMove,
  onCopy,
  onRemove,
}: Props) {
  const set = (change: Partial<TourOfferItineraryDayInput>) => onChange({ ...day, ...change });
  const endDay = startDay + Math.max(day.duration, 1) - 1;
  const dayLabel = endDay > startDay ? `Days ${startDay}–${endDay}` : `Day ${startDay}`;

  return (
    <Accordion expanded={expanded} onChange={(_event, next) => onExpand(next)} disableGutters variant="outlined">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={2} alignItems="center" flex={1} minWidth={0}>
          <Chip size="small" label={dayLabel} color={problem ? 'error' : 'default'} />
          <Box minWidth={0}>
            <Typography noWrap>{day.title || 'Untitled day'}</Typography>
            <Typography variant="caption" color={problem ? 'error' : 'text.secondary'} noWrap>
              {problem ?? (day.locations.map(locationLabel).join(' · ') || day.accommodation || '')}
            </Typography>
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2.5}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 9 }}>
              <TextField
                label="Title"
                value={day.title}
                onChange={(event) => set({ title: event.target.value })}
                error={!day.title.trim()}
                helperText={!day.title.trim() ? 'Required' : undefined}
                required
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                type="number"
                label="Days"
                value={day.duration}
                onChange={(event) => set({ duration: Math.trunc(Number(event.target.value)) })}
                inputProps={{ min: 1, step: 1 }}
                error={!Number.isInteger(day.duration) || day.duration < 1}
                fullWidth
              />
            </Grid>
          </Grid>
          <MarkdownEditor
            label="Description"
            showLabel
            value={day.description}
            onChange={(description) => set({ description })}
          />
          <NativeTourLocationSearch
            label="Locations visited"
            value={day.locations}
            onChange={(locations) => set({ locations })}
            helperText="They make up the countries visited"
          />
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <TextField
                label="Accommodation"
                value={day.accommodation ?? ''}
                onChange={(event) =>
                  set({ accommodation: event.target.value.trim() === '' ? null : event.target.value })
                }
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <ToggleButtonGroup
                size="small"
                value={day.meals}
                onChange={(_event, meals: Array<string>) =>
                  set({ meals: ITINERARY_MEALS.filter((meal) => meals.includes(meal)) })
                }
                aria-label="Meals"
              >
                {ITINERARY_MEALS.map((meal) => (
                  <ToggleButton key={meal} value={meal}>
                    {meal}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid>
          </Grid>

          <Box>
            <Typography variant="subtitle2" mb={1}>
              Images
            </Typography>
            <Grid container spacing={2}>
              {day.carouselImages.map((image, index) => (
                <Grid key={index} size={{ xs: 12, sm: 6, lg: 4 }}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Stack spacing={1}>
                      <NativeTourImageField
                        label={`Image ${index + 1}`}
                        value={image.svcImageId || null}
                        error={!image.svcImageId}
                        removable={false}
                        onChange={(svcImageId) =>
                          set({
                            carouselImages: day.carouselImages.map((other, i) =>
                              i === index ? { ...other, svcImageId: svcImageId ?? '' } : other,
                            ),
                          })
                        }
                      />
                      <Stack direction="row" spacing={1}>
                        <TextField
                          size="small"
                          label="Caption"
                          value={image.caption ?? ''}
                          onChange={(event) =>
                            set({
                              carouselImages: day.carouselImages.map((other, i) =>
                                i === index ? { ...other, caption: event.target.value || null } : other,
                              ),
                            })
                          }
                          fullWidth
                        />
                        <NativeTourRemoveButton
                          label="Remove image"
                          onClick={() => set({ carouselImages: day.carouselImages.filter((_, i) => i !== index) })}
                        />
                      </Stack>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            <Button
              size="small"
              startIcon={<AddIcon />}
              sx={{ mt: 1 }}
              onClick={() => set({ carouselImages: [...day.carouselImages, { svcImageId: '', caption: null }] })}
            >
              Add image
            </Button>
          </Box>

          <Box>
            <Typography variant="subtitle2" mb={1}>
              Special call-outs
            </Typography>
            <Stack spacing={1.5}>
              {day.specialCallOuts.map((callOut, index) => {
                const update = (change: Partial<TourOfferItineraryDayInput['specialCallOuts'][number]>) =>
                  set({
                    specialCallOuts: day.specialCallOuts.map((other, i) =>
                      i === index ? { ...other, ...change } : other,
                    ),
                  });
                return (
                  <Paper key={index} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack spacing={1.5}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                        <TextField
                          select
                          size="small"
                          label="Image"
                          value={callOut.svcImageId ?? NO_IMAGE}
                          onChange={(event) =>
                            update({ svcImageId: event.target.value === NO_IMAGE ? null : event.target.value })
                          }
                          sx={{ minWidth: 240 }}
                        >
                          <MenuItem value={NO_IMAGE}>No image</MenuItem>
                          <MenuItem value={DEFAULT_HOST_IMAGE}>Default host image</MenuItem>
                          {hosts.map((host) => (
                            <MenuItem key={host.svcImageId} value={host.svcImageId}>
                              {host.name}
                            </MenuItem>
                          ))}
                        </TextField>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={callOut.isFlash}
                              onChange={(event) => update({ isFlash: event.target.checked })}
                            />
                          }
                          label="Flash only"
                        />
                        <Box flex={1} />
                        <NativeTourRemoveButton
                          label="Remove call-out"
                          onClick={() => set({ specialCallOuts: day.specialCallOuts.filter((_, i) => i !== index) })}
                        />
                      </Stack>
                      <MarkdownEditor value={callOut.description} onChange={(description) => update({ description })} />
                      {!callOut.description.trim() && (
                        <Typography variant="caption" color="error">
                          Needs a description
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
            <Button
              size="small"
              startIcon={<AddIcon />}
              sx={{ mt: 1 }}
              onClick={() =>
                set({
                  specialCallOuts: [...day.specialCallOuts, { svcImageId: null, description: '', isFlash: false }],
                })
              }
            >
              Add call-out
            </Button>
          </Box>

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Tooltip title="Move up">
              <span>
                <IconButton aria-label="Move day up" disabled={isFirst} onClick={() => onMove(-1)}>
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move down">
              <span>
                <IconButton aria-label="Move day down" disabled={isLast} onClick={() => onMove(1)}>
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Copy as a new day after this one">
              <IconButton aria-label="Copy day" onClick={onCopy}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <NativeTourRemoveButton label="Remove day" onClick={onRemove} />
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
